import { useState, useRef, DragEvent, ChangeEvent } from "react";
import { X, UploadCloud } from "lucide-react";
import * as XLSX from "xlsx";
import { Keyword } from "@/types";

const SOURCE_LABEL: Record<string, string> = {
  seo: "SEO Seeds", sem: "SEM", opinion: "Public Opinion",
};

interface Props {
  source: "seo" | "sem" | "opinion";
  keywords: Keyword[];
  setKeywords: React.Dispatch<React.SetStateAction<Keyword[]>>;
  onClose: () => void;
}

/* ─── Smart auto-classification engine ───────────────────────── */
/**
 * Classifies a keyword into one of the five strategy boards by testing
 * regex patterns in priority order. Returns the intentBoard key string.
 */
export function autoCategorizeKeyword(keyword: string): string {
  const kw = keyword.toLowerCase();

  // Priority 1 — Brand Defense: brand word + trust/negative signal
  if (
    /easycash|easicash/.test(kw) &&
    /aman|resmi|legal|terdaftar ojk|penipu|ilegal|kasus|gagal bayar|galbay|dc lapangan|debt collector|bocor data/.test(kw)
  ) return "defensif";

  // Priority 2 — Competitor: comparison relation OR rival brand
  if (
    /\bvs\b|atau|perbandingan|alternatif|lebih baik mana/.test(kw) ||
    /adakami|akulaku|kredivo|shopeepaylater|kredit pintar|rupiah cepat/.test(kw)
  ) return "kompetitor";

  // Priority 3 — Conversion: action/benefit/core product words
  if (
    /daftar|download|aplikasi|pengajuan/.test(kw) ||
    /cepat cair|bunga rendah|limit tinggi|tanpa agunan|\bkta\b|syarat|tenor/.test(kw) ||
    /pinjam uang online|pinjaman tunai/.test(kw)
  ) return "konversi";

  // Priority 4 — Solution & Trust: scenario or trust signals
  if (
    /modal usaha|biaya rumah sakit|bayar sekolah|butuh dana mendesak/.test(kw) ||
    /review|pengalaman|testimoni|terbukti/.test(kw)
  ) return "solusi";

  // Priority 5 — Education: question/guide words or financial literacy terms
  if (
    /apa itu|bagaimana|mengapa|\bcara\b|tips|panduan/.test(kw) ||
    /bi checking|slik ojk|skor kredit|literasi keuangan|bunga majemuk/.test(kw)
  ) return "edukasi";

  // Fallback
  return "unclassified";
}

/* ─── GSC row shape ───────────────────────────────────────────── */
interface ParsedGscRow {
  keyword: string;
  clicks: number;
  impressions: number;
  siteCtr: string;
}

/* ─── Keyword sanitizer ───────────────────────────────────────── */
/**
 * Returns true if the candidate string is a real human-readable search
 * keyword, not a title row, a stray number, or a noisy cell.
 */
const TITLE_BLACKLIST = /关键词表|汇总|明细|数据报告|报表|sheet|统计|合计|total|summary/i;

function isValidKeyword(raw: unknown): raw is string {
  if (typeof raw !== "string") return false;
  const s = raw.trim();
  if (s.length < 2) return false;                   // too short
  if (!isNaN(Number(s))) return false;              // pure number / decimal
  if (TITLE_BLACKLIST.test(s)) return false;        // obvious title cell
  return true;
}

/* ─── Smart header detection + full-row GSC parser ───────────── */
/**
 * Phase 1 — read as 2-D array and scan downward to locate the header row
 *            that contains a keyword-column label.
 * Phase 2 — once the header row is found, map every subsequent row through
 *            the sanitizer to produce clean ParsedGscRow objects.
 */
function parseXlsxBuffer(buffer: ArrayBuffer): ParsedGscRow[] {
  const wb    = XLSX.read(new Uint8Array(buffer), { type: "array" });
  const sheet = wb.Sheets[wb.SheetNames[0]];

  // Read the whole sheet as a raw 2-D array (no header inference yet)
  const grid: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
  if (grid.length === 0) return [];

  /* ── Phase 1: find the header row ── */
  const KW_HEADER_PATTERN = /^(query|keyword|keywords|关键词|搜索词|search query)$/i;
  const CLICK_PATTERN      = /^(clicks?|点击量|点击次数)$/i;
  const IMP_PATTERN        = /^(impressions?|展示量|曝光量|曝光次数)$/i;
  const CTR_PATTERN        = /^(ctr|site\s*ctr|点击率)$/i;

  let headerRowIdx = -1;
  let kwColIdx     = -1;
  let clkColIdx    = -1;
  let impColIdx    = -1;
  let ctrColIdx    = -1;

  for (let r = 0; r < Math.min(grid.length, 20); r++) {
    const row = grid[r];
    const kwIdx = row.findIndex(
      cell => typeof cell === "string" && KW_HEADER_PATTERN.test(cell.trim())
    );
    if (kwIdx !== -1) {
      headerRowIdx = r;
      kwColIdx     = kwIdx;
      // Scan same row for metric columns
      row.forEach((cell, i) => {
        if (typeof cell !== "string") return;
        const v = cell.trim();
        if (CLICK_PATTERN.test(v)) clkColIdx = i;
        if (IMP_PATTERN.test(v))   impColIdx = i;
        if (CTR_PATTERN.test(v))   ctrColIdx = i;
      });
      break;
    }
  }

  // No explicit header found — fall back to column 0 as keyword, no metrics
  if (headerRowIdx === -1) {
    headerRowIdx = 0;
    kwColIdx     = 0;
  }

  /* ── Phase 2: parse data rows with sanitizer ── */
  const results: ParsedGscRow[] = [];

  for (let r = headerRowIdx + 1; r < grid.length; r++) {
    const row = grid[r];
    const kwRaw = row[kwColIdx];

    if (!isValidKeyword(kwRaw)) continue;  // sanitizer gate

    const keyword = kwRaw.trim();

    const toNum = (idx: number): number => {
      if (idx === -1) return 0;
      const v = row[idx];
      const n = Number(v);
      return isNaN(n) ? 0 : n;
    };

    const ctrRaw = ctrColIdx !== -1 ? row[ctrColIdx] : undefined;
    const siteCtr =
      ctrRaw !== undefined && ctrRaw !== ""
        ? typeof ctrRaw === "number"
          ? `${(ctrRaw * 100).toFixed(2)}%`   // GSC stores CTR as 0.054 → "5.40%"
          : String(ctrRaw)
        : "—";

    results.push({
      keyword,
      clicks:      toNum(clkColIdx),
      impressions: toNum(impColIdx),
      siteCtr,
    });
  }

  return results;
}

export default function ImportModal({ source, keywords, setKeywords, onClose }: Props) {
  const [importText, setImportText] = useState("");
  const [richRows, setRichRows]     = useState<ParsedGscRow[]>([]);
  const [importing, setImporting]   = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ── File readers ────────────────────────────────────────────── */
  function readFile(file: File) {
    const isXlsx = /\.(xlsx|xls)$/i.test(file.name);
    const isCsv  = /\.csv$/i.test(file.name);
    const isTxt  = /\.txt$/i.test(file.name);

    if (isXlsx || isCsv) {
      // Parse as binary with XLSX to extract full structured rows
      const reader = new FileReader();
      reader.onload = e => {
        const buffer = e.target?.result as ArrayBuffer;
        const rows = parseXlsxBuffer(buffer);
        if (rows.length > 0) {
          // Rich structured import — bypass textarea
          setRichRows(prev => {
            const existingKws = new Set(prev.map(r => r.keyword.toLowerCase()));
            const newRows = rows.filter(r => !existingKws.has(r.keyword.toLowerCase()));
            return [...prev, ...newRows];
          });
        } else {
          // Empty or unparseable — fall back to plain text
          const decoder = new TextDecoder("utf-8");
          const text = decoder.decode(buffer);
          const lines = text
            .split(/\r?\n/)
            .map(s => s.trim().replace(/^["']|["']$/g, ""))
            .filter(Boolean)
            .join("\n");
          setImportText(prev => (prev.trim() ? `${prev.trim()}\n${lines}` : lines));
        }
      };
      reader.readAsArrayBuffer(file);
    } else if (isTxt) {
      // Plain text — append to textarea as before
      const reader = new FileReader();
      reader.onload = e => {
        const text = (e.target?.result as string).trim();
        setImportText(prev => (prev.trim() ? `${prev.trim()}\n${text}` : text));
      };
      reader.readAsText(file, "utf-8");
    }
  }

  function handleFileInputChange(e: ChangeEvent<HTMLInputElement>) {
    Array.from(e.target.files ?? []).forEach(readFile);
    e.target.value = "";
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(true);
  }
  function handleDragLeave() { setIsDragging(false); }
  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    Array.from(e.dataTransfer.files).forEach(readFile);
  }

  async function handleImport() {
    const hasRich = richRows.length > 0;
    const hasText = importText.trim().length > 0;
    if (!hasRich && !hasText) return;

    setImporting(true);
    await new Promise(r => setTimeout(r, 400));

    const existingSet = new Set(keywords.map(k => k.keyword.toLowerCase().trim()));
    const batchSeen   = new Set<string>();
    const toAdd: Keyword[] = [];
    let duplicates = 0;
    const today = new Date().toISOString().split("T")[0];

    if (hasRich) {
      /* ── Structured path: XLSX / CSV with GSC columns ── */
      richRows.forEach(row => {
        const norm = row.keyword.toLowerCase().trim();
        if (!norm) return;
        if (existingSet.has(norm) || batchSeen.has(norm)) {
          duplicates++;
        } else {
          batchSeen.add(norm);
          toAdd.push({
            id:          `k_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
            raw:         row.keyword,
            keyword:     row.keyword,
            intentBoard: autoCategorizeKeyword(row.keyword),
            source,
            clicks:      row.clicks     || undefined,
            impressions: row.impressions || undefined,
            siteCtr:     row.siteCtr !== "—" ? row.siteCtr : undefined,
            createdAt:   today,
          });
        }
      });
    } else {
      /* ── Plain-text path: one keyword per line ── */
      importText
        .split("\n")
        .map(l => l.trim())
        .filter(l => {
          if (!l) return false;
          const key = l.toLowerCase();
          if (batchSeen.has(key)) return false;
          batchSeen.add(key);
          return true;
        })
        .forEach(line => {
          if (existingSet.has(line.toLowerCase())) {
            duplicates++;
          } else {
            toAdd.push({
              id:          `k_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
              raw:         line,
              keyword:     line,
              intentBoard: autoCategorizeKeyword(line),
              source,
              createdAt:   today,
            });
          }
        });
    }

    setKeywords(prev => [...prev, ...toAdd]);
    setImporting(false);
    onClose();

    const msg = duplicates > 0
      ? `Import complete! Added ${toAdd.length} new keyword${toAdd.length !== 1 ? "s" : ""}. Skipped ${duplicates} duplicate${duplicates !== 1 ? "s" : ""}.`
      : `Import complete! Added ${toAdd.length} new keyword${toAdd.length !== 1 ? "s" : ""}.`;
    window.alert(msg);
  }

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-semibold text-gray-900">Import to {SOURCE_LABEL[source]}</h3>
            <p className="text-xs text-gray-400 mt-0.5">One keyword per line · Strategy auto-classified on import</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
            <X size={18} />
          </button>
        </div>

          {/* ── File dropzone ───────────────────────────────────── */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`mb-3 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-5 cursor-pointer transition-colors ${
              isDragging
                ? "border-indigo-400 bg-indigo-50"
                : "border-gray-200 bg-gray-50 hover:border-indigo-300 hover:bg-indigo-50/40"
            }`}
          >
            <UploadCloud size={28} className={isDragging ? "text-indigo-500" : "text-gray-400"} />
            <p className="text-xs font-medium text-gray-500 text-center leading-relaxed">
              Click to upload or drag and drop
              <span className="block text-gray-400 font-normal">XLSX · CSV · TXT — supports GSC exports with Query / Clicks / Impressions / CTR</span>
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,.txt"
              multiple
              className="hidden"
              onChange={handleFileInputChange}
            />
          </div>

          {/* ── Rich-rows preview (shown when XLSX/CSV with headers was loaded) ── */}
          {richRows.length > 0 && (
            <div className="mb-3 flex items-center justify-between px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-xl">
              <span className="text-xs text-indigo-700 font-medium">
                ✅ {richRows.length} rows detected with GSC data (Query · Clicks · Impressions · CTR)
              </span>
              <button
                onClick={() => setRichRows([])}
                className="text-[11px] text-indigo-400 hover:text-red-500 transition-colors ml-2 flex-shrink-0"
              >
                Clear
              </button>
            </div>
          )}

          {/* ── Manual textarea (plain keyword list; hidden when rich rows are loaded) ── */}
          {richRows.length === 0 && (
            <textarea
              value={importText}
              onChange={e => setImportText(e.target.value)}
              rows={6}
              placeholder={"pinjaman online aman\nkredit usaha kecil\ncara lapor pinjol ilegal"}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none font-mono mb-4"
            />
          )}

          <div className="flex gap-3 mt-4">
            <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={importing || (richRows.length === 0 && !importText.trim())}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              {importing ? "Importing..." : `Import${richRows.length > 0 ? ` (${richRows.length} rows)` : ""}`}
            </button>
          </div>
      </div>
    </div>
  );
}
